import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  VocabularyListProps,
  GetVocabularyProgressResponse,
  VocabularyProgressItem,
} from '@dreamai/types';
import api from '../../../../api';
import './vocabulary-list.scss';

interface VocabularyListComponentProps extends VocabularyListProps {
  threadId?: string;
  updateThreadViewVisualDescription?: (
    visualDescription: string,
  ) => Promise<void>;
}

type FilterType = 'all' | 'overdue' | 'mastered' | 'vocabulary' | 'grammar';

const VocabularyList: React.FC<VocabularyListComponentProps> = ({
  languageLearningId,
  itemType = 'all',
  title,
  threadId,
  updateThreadViewVisualDescription,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [items, setItems] = useState<VocabularyProgressItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [summary, setSummary] = useState<{
    totalVocabulary: number;
    totalGrammar: number;
    overdueCount: number;
    masteredCount: number;
  } | null>(null);
  const [language, setLanguage] = useState<string | undefined>();
  const [cefrLevel, setCefrLevel] = useState<string | undefined>();
  const [filter, setFilter] = useState<FilterType>(
    itemType === 'all' ? 'overdue' : itemType,
  );
  const [showMastered, setShowMastered] = useState(false);

  const fetchVocabulary = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const token = await getAccessTokenSilently();
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      // Determine item type filter based on current filter
      let itemTypeParam: 'vocabulary' | 'grammar' | 'all' = 'all';
      if (filter === 'vocabulary') itemTypeParam = 'vocabulary';
      else if (filter === 'grammar') itemTypeParam = 'grammar';

      // Build query params
      const params: Record<string, string> = {
        languageLearningId,
        itemType: itemTypeParam,
        showMastered: filter === 'mastered' ? 'true' : showMastered.toString(),
        page: page.toString(),
        limit: '20',
        sortBy: filter === 'overdue' ? 'overdue' : 'recent',
      };

      const response = await api.get<GetVocabularyProgressResponse>(
        '/language-learning/vocabulary',
        { params },
      );

      if (response.data.success) {
        let filteredItems = response.data.items;

        // Apply client-side filtering for overdue
        if (filter === 'overdue') {
          filteredItems = filteredItems.filter((item) => item.daysOverdue >= 0);
        }

        setItems(filteredItems);
        setTotalPages(response.data.pagination.totalPages);
        setSummary(response.data.summary);
        setLanguage(response.data.language);
        setCefrLevel(response.data.cefrLevel);
      } else {
        setError(response.data.error || 'Failed to load vocabulary');
      }
    } catch (err) {
      console.error('Error fetching vocabulary:', err);
      setError('Failed to load vocabulary');
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, languageLearningId, page, filter, showMastered]);

  useEffect(() => {
    fetchVocabulary();
  }, [fetchVocabulary]);

  // Update visual description when data loads
  useEffect(() => {
    if (updateThreadViewVisualDescription && language && summary) {
      updateThreadViewVisualDescription(
        `Vocabulary list for ${language} (${cefrLevel || 'unknown level'}): ${summary.totalVocabulary} words, ${summary.totalGrammar} grammar concepts, ${summary.overdueCount} overdue`,
      );
    }
  }, [language, cefrLevel, summary, updateThreadViewVisualDescription]);

  const getOverdueLabel = (daysOverdue: number): string => {
    if (daysOverdue < 0) {
      const daysUntil = Math.abs(daysOverdue);
      if (daysUntil === 1) return 'Due tomorrow';
      return `Due in ${daysUntil} days`;
    }
    if (daysOverdue === 0) return 'Due today';
    if (daysOverdue === 1) return '1 day overdue';
    return `${daysOverdue} days overdue`;
  };

  const getOverdueClass = (daysOverdue: number): string => {
    if (daysOverdue < 0) return 'due-soon';
    if (daysOverdue === 0) return 'due-today';
    if (daysOverdue <= 3) return 'slightly-overdue';
    if (daysOverdue <= 7) return 'overdue';
    return 'very-overdue';
  };

  const getSuccessRate = (item: VocabularyProgressItem): string => {
    if (item.timesTested === 0) return 'Not tested';
    const rate = Math.round((item.timesCorrect / item.timesTested) * 100);
    return `${rate}% (${item.timesCorrect}/${item.timesTested})`;
  };

  if (loading && items.length === 0) {
    return (
      <div className='vocabulary-list loading'>
        <div className='loading-spinner'>Loading vocabulary...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='vocabulary-list error'>
        <div className='error-message'>{error}</div>
        <button onClick={fetchVocabulary} className='retry-button'>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className='vocabulary-list'>
      <div className='vocabulary-header'>
        <h2 className='vocabulary-title'>
          {title || `${language || 'Language'} Vocabulary`}
          {cefrLevel && <span className='cefr-badge'>{cefrLevel}</span>}
        </h2>

        {summary && (
          <div className='vocabulary-summary'>
            <span className='summary-item vocab-count'>
              {summary.totalVocabulary} words
            </span>
            <span className='summary-item grammar-count'>
              {summary.totalGrammar} grammar
            </span>
            <span className='summary-item overdue-count'>
              {summary.overdueCount} overdue
            </span>
            <span className='summary-item mastered-count'>
              {summary.masteredCount} mastered
            </span>
          </div>
        )}
      </div>

      <div className='vocabulary-filters'>
        <div className='filter-buttons'>
          <button
            className={`filter-btn ${filter === 'overdue' ? 'active' : ''}`}
            onClick={() => {
              setFilter('overdue');
              setPage(1);
            }}
          >
            Overdue
          </button>
          <button
            className={`filter-btn ${filter === 'vocabulary' ? 'active' : ''}`}
            onClick={() => {
              setFilter('vocabulary');
              setPage(1);
            }}
          >
            Vocabulary
          </button>
          <button
            className={`filter-btn ${filter === 'grammar' ? 'active' : ''}`}
            onClick={() => {
              setFilter('grammar');
              setPage(1);
            }}
          >
            Grammar
          </button>
          <button
            className={`filter-btn ${filter === 'mastered' ? 'active' : ''}`}
            onClick={() => {
              setFilter('mastered');
              setPage(1);
            }}
          >
            Mastered
          </button>
          <button
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => {
              setFilter('all');
              setPage(1);
            }}
          >
            All
          </button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className='vocabulary-empty'>
          <p>
            {filter === 'overdue'
              ? 'No overdue items. Great job staying on top of your reviews!'
              : filter === 'mastered'
                ? 'No mastered items yet. Keep practicing!'
                : 'No items found.'}
          </p>
        </div>
      ) : (
        <>
          <div className='vocabulary-items'>
            {items.map((item) => (
              <div
                key={item.id}
                className={`vocabulary-item ${item.mastered ? 'mastered' : ''} ${item.itemType}`}
              >
                <div className='item-main'>
                  <div className='item-word'>
                    <span className='word-text'>{item.item}</span>
                    <span className={`item-type-badge ${item.itemType}`}>
                      {item.itemType}
                    </span>
                  </div>
                  {item.itemTranslation && (
                    <div className='item-translation'>
                      {item.itemTranslation}
                    </div>
                  )}
                </div>

                <div className='item-stats'>
                  <div
                    className={`overdue-indicator ${getOverdueClass(item.daysOverdue)}`}
                  >
                    {item.mastered
                      ? '✓ Mastered'
                      : getOverdueLabel(item.daysOverdue)}
                  </div>
                  <div className='success-rate'>{getSuccessRate(item)}</div>
                </div>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className='vocabulary-pagination'>
              <button
                className='pagination-btn'
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </button>
              <span className='pagination-info'>
                Page {page} of {totalPages}
              </span>
              <button
                className='pagination-btn'
                disabled={page === totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default VocabularyList;
